import { ReactNode, useCallback, useId, useState } from 'react';
import { useField } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/plain.css';
import classnames from 'classnames';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { Theme } from '@app/theme';
import { ErrorMsg } from './ErrorMsg';
import { useFormikError } from './FormikFields';
import { InputLabel } from './InputLabel';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
    width: '100%',
  },
  noMargin: {
    marginBottom: 0,
    marginTop: 0,
  },
  disabled: {
    opacity: 0.5,
  },
}));

interface Props {
  name: string;
  phoneCountryCodeDefault?: CountryCode;
  label?: string;
  className?: string;
  noMargin?: boolean;
  onCountryChange?: (countryKey: string) => any;
  disabled?: boolean;
  rightAction?: ReactNode;
}

const getInitialCountryCode = (phoneNumber: string) => {
  if (!phoneNumber) {
    return 'fr';
  }
  const parsed = parsePhoneNumberFromString(phoneNumber);
  if (!parsed || !parsed.country) {
    return 'fr';
  }
  return parsed.country.toLowerCase();
};

export const PhoneNumberInput = ({
  name,
  label,
  className,
  noMargin,
  onCountryChange,
  disabled,
  rightAction,
}: Props) => {
  const theme = useTheme() as Theme;
  const classes = useStyles({ theme });
  const errorMsg = useFormikError({ name });
  const id = useId();

  const [field, _, { setValue }] = useField(name);
  const [{ value: initialCountryKey }] = useField('countryKey');

  const initialCountryCode = initialCountryKey
    ? initialCountryKey.toLowerCase()
    : getInitialCountryCode(field.value);

  const [country, setCountry] = useState(initialCountryCode);

  const fieldBaseStyle = {
    backgroundColor: theme.colors.white,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.colors.grayBorder,
    borderRadius: theme.borderRadius.std,
    minHeight: theme.height.regular,
    transition: 'border-color 0.3s',
    boxShadow: 'none',
  };

  const onChange = useCallback(
    (value: string, countryData: CountryData) => {
      setCountry(countryData.countryCode);
      if (onCountryChange) {
        onCountryChange((countryData.countryCode || '').toUpperCase());
      }
      if (value.startsWith('0') && !value.startsWith('00')) {
        setValue('+' + countryData.dialCode + value.substring(1));
        return;
      }

      setValue('+' + value);
    },
    [setValue],
  );

  return (
    <div
      className={classnames(classes.container, className, {
        [classes.noMargin]: noMargin,
        [classes.disabled]: !!disabled,
      })}
    >
      <InputLabel htmlFor={id} label={label} rightAction={rightAction} />
      <PhoneInput
        {...field}
        onChange={onChange}
        country={country}
        countryCodeEditable
        autoFormat={true}
        inputProps={{
          name: field.name,
          id,
        }}
        masks={{ ci: '.. .. .. .. ..' }}
        inputStyle={{
          ...theme.fonts.caption,
          ...fieldBaseStyle,
          paddingLeft: theme.marginBase * 8,
          width: '100%',
          [`@media (maxWidth: ${theme.breackPoints.mobile - 1}px)`]: {
            //disable zoom for iPhone users
            fontSize: 16,
          },
        }}
        buttonStyle={{
          ...fieldBaseStyle,
          paddingInline: theme.marginBase,
        }}
        disabled={disabled}
      />
      {errorMsg && <ErrorMsg error={errorMsg} />}
    </div>
  );
};
